import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSearchParams} from 'react-router-dom';

import {broker} from "../../../config/broker.ts";
import {parseHandoffQuery} from "../services/HandoffQueryParser.ts";
import {returnToBroker} from "../services/BrokerReturnService.ts";
import {
    resumeAnyPlatformSession,
    signInErrorMessage,
    signInWithCredentials
} from "../services/HandoffSignInService.ts";
import {readRememberedAccount} from "../services/PlatformSessionStore.ts";
import {HANDOFF_MESSAGES} from "../config/messages.ts";
import {HandoffCredentials} from "../types.ts";

export type HandoffState =
    | { phase: 'error', message: string }
    | { phase: 'checking' }
    | { phase: 'password', signInError: string | null }
    | { phase: 'redirecting' };

export const useHandoff = () => {
    const [searchParams] = useSearchParams();
    const query = useMemo(() => parseHandoffQuery(searchParams), [searchParams]);
    const brokerBaseUrl = broker.baseUrl;

    const [state, setState] = useState<HandoffState>(() => {
        if (!query.ok) return {phase: 'error', message: query.error};
        if (!brokerBaseUrl) return {phase: 'error', message: HANDOFF_MESSAGES.unavailable};
        return {phase: 'checking'};
    });
    const started = useRef(false);

    useEffect(() => {
        if (state.phase !== 'checking' || started.current || !query.ok) {
            return;
        }
        started.current = true;

        const {request, clients} = query.value;
        resumeAnyPlatformSession(clients)
            .then((tokens) => {
                if (tokens === null) {
                    setState({phase: 'password', signInError: null});
                    return;
                }
                setState({phase: 'redirecting'});
                returnToBroker(brokerBaseUrl, request, tokens);
            })
            .catch(() => setState({phase: 'password', signInError: null}));
    }, [state.phase, query, brokerBaseUrl]);

    const initialEmail = useMemo(() => {
        if (!query.ok) return '';
        const remembered = readRememberedAccount();
        return remembered && query.value.clients.some((c) => c.poolId === remembered.poolId) ? remembered.email : '';
    }, [query]);

    const handleSignIn = useCallback(async (values: HandoffCredentials) => {
        if (!query.ok) {
            return;
        }
        setState({phase: 'password', signInError: null});
        try {
            const tokens = await signInWithCredentials(query.value.clients, values);
            setState({phase: 'redirecting'});
            returnToBroker(brokerBaseUrl, query.value.request, tokens);
        } catch (e) {
            setState({phase: 'password', signInError: signInErrorMessage(e)});
        }
    }, [query, brokerBaseUrl]);

    return {
        state,
        initialEmail,
        handleSignIn,
    };
};
